/**
 * notifications — the Notification Centre, preferences, and device registration.
 *
 * Every route here is about the caller's OWN notifications. There is no client
 * function that reads or configures anyone else's, because there is no route
 * that would accept it.
 *
 * `target` is a TYPED pointer, never a URL. Each client maps a kind to its own
 * route, which is what lets the same notification open the right screen in the
 * Admin app and in the Worker app — and what stops a notification from ever
 * being a link to somewhere unvetted.
 */
import { del, get, post, put } from './transport.js';
/** `GET /notifications` → `{ notifications, unread }` (needs `notification:read`). */
export async function list(filters = {}, options) {
    return get('/notifications', {
        ...options,
        query: { ...filters },
    });
}
/**
 * `GET /notifications/unread-count` → `{ unread }`.
 *
 * Separate from the list because the shell polls it constantly and does not need
 * the bodies to draw a number.
 */
export async function unreadCount(options) {
    const body = await get('/notifications/unread-count', options);
    return body.unread;
}
/** `POST /notifications/:id/read`. 404 for a notification that is not yours. */
export async function markRead(id, options) {
    return post(`/notifications/${id}/read`, {}, options);
}
export async function markAllRead(options) {
    return post('/notifications/read-all', {}, options);
}
/** `GET /notifications/preferences` → every category, with your choice applied. */
export async function preferences(options) {
    return get('/notifications/preferences', options);
}
/**
 * `PUT /notifications/preferences`.
 *
 * No user id anywhere: preferences are personal by construction, so no request
 * shape can write onto somebody else's account. Mandatory categories are
 * ignored rather than rejected, so sending the whole list back always works.
 *
 * `quietHours` is optional: omitted means "leave the stored window alone", so a
 * caller that predates quiet hours can still save per-category toggles.
 */
export async function updatePreferences(input, options) {
    return put('/notifications/preferences', input, options);
}
/**
 * `GET /notifications/preference-suggestions` — suggested changes to your own
 * preferences. Nothing is applied: accepting one is an ordinary
 * `updatePreferences` call.
 */
export async function preferenceSuggestions(options) {
    return get('/notifications/preference-suggestions', options);
}
/** `POST /notifications/preference-suggestions/dismiss` — "not this one", remembered. */
export async function dismissPreferenceSuggestion(key, options) {
    await post('/notifications/preference-suggestions/dismiss', { key }, options);
}
/** `POST /notifications/devices` — register or refresh this install for push. */
export async function registerDevice(input, options) {
    return post('/notifications/devices', input, options);
}
/**
 * `DELETE /notifications/devices/:deviceId` — retire this install on sign-out.
 *
 * Drops the push token rather than keeping a dead address the server would try
 * to reach forever. 204 on success.
 */
export async function unregisterDevice(deviceId, options) {
    await del(`/notifications/devices/${encodeURIComponent(deviceId)}`, options);
}
//# sourceMappingURL=notifications.js.map